<template>
    <div class="custom-drug-validation-plot">
        <div v-if="!isEditing" class="legendContainer">
             <div class="channel" v-for="(channelName,i) in this.channels.names" :key="i">
                <div class="channel-label">
                    {{channelName}}
                    <hr :style="{backgroundColor: colors[i], height: '3px', width: '70%', borderWidth: 0}">
                </div>
            </div>
        </div>
        <svg ref="svg" :width="_size.width" :height="_size.height">
            <clipPath id="content-clip">
                <rect />
            </clipPath>
        </svg>
    </div>
</template>

<script>
import d3 from '@/utils/d3importer'
import widgetContent from '@/mixins/widgetContent'
import { convertUnits, unit_nML, convertNumberToNewRange, calculateDrugChannelBlockPercentage } from '@/utils/UnitConverer'

export default {
    name: 'CustomDrugValidationPlot',
    mixins: [widgetContent],
    props: {
        channels: Object,
        hValues: Array,
        ic50Values: Array,
        concentrations: Array,
        concentrationUnits: String,
        molecularWeight: Number,
        width: Number,
        height: Number,
        isEditing: Boolean
    },
    data() {
        return {
            xAxisLabel: null
        }
    },
    mounted() {
        this.x = d3.scaleLinear()
        this.y = d3.scaleLinear()
        this.axisFuncs = {
            x: d3.axisBottom(this.x),
            y: d3.axisLeft(this.y)
        }
        this.updateChart()
    },
    computed: {
        _size() {
            let w = this.width || this.size.width
            let h = this.height || this.size.height
            let controlsWidth = this.controls ? h : 0
            return {
                width: w - controlsWidth,
                height: h
            }
        },
        contentSize() {
            return {
                width: Math.max(0, this._size.width - this.margin.left - this.margin.right),
                height: Math.max(0, this._size.height - this.margin.top - this.margin.bottom)
            }
        },
        margin() {
            let m = { left: 0, top: 0, bottom: 0, right: 0 }
            m.bottom = 50
            m.left = 70
            m.right = 10

            if (this.forcedMargin) {
                Object.keys(this.forcedMargin).forEach((k) => m[k] = this.forcedMargin[k])
            }
            return m
        },
        colors() {
            let indices = Array.from(this.channels.names.keys())
            return indices.map(i => d3.schemePaired[i])
        },
        validationCurves() {
            let convertedConcentrations = this.concentrations;
            convertedConcentrations.sort((a, b) => a - b);

            // Here we are generating 100 points in x-axis between the min and max concentration
            let xAxisPoints = [];
            const oldMin = 0;
            const oldMax = 100;
            const newMin = 0; //convertedConcentrations[0];
            // leave some space after last concentration inside the plot
            const newMax = convertedConcentrations[convertedConcentrations.length - 1] + convertedConcentrations[0];
            for (let i=oldMin; i<oldMax; i++) {
                const xToAdd = convertNumberToNewRange(i, oldMin, oldMax, newMin, newMax);
                xAxisPoints.push(xToAdd);
            }
            convertedConcentrations.slice(1, -1).forEach(concentration => {
                xAxisPoints.push(concentration);
            });
            xAxisPoints.sort((a, b) => a - b);

            let curves = []
            for (let chIdx = 0; chIdx < this.channels.names.length; chIdx ++) {
                let computedValues = []
                xAxisPoints.forEach((concentrationValue, i) => {
                    // here IC50 are always in nmol/L, so we need to convert them to concentrations units
                    let ic50ValueConverted = convertUnits(this.ic50Values[chIdx], this.molecularWeight, unit_nML, this.concentrationUnits);
                    let channelBlock = calculateDrugChannelBlockPercentage(concentrationValue, ic50ValueConverted, this.hValues[chIdx]);
                    computedValues.push({c: concentrationValue, block: channelBlock});
                })
                curves.push({channel: this.channels.names[chIdx], data: computedValues});
            }
            return curves
        }
    },
    methods: {
        updateChart() {
            let allValues = this.validationCurves.map(channel => channel.data).flat()
            this.x.range([0,this.contentSize.width]).domain(d3.extent(allValues,(d)=>+d.c))
            let y = this.y.range([this.contentSize.height,0]).domain([0,100])
            y.domain([ y.domain()[0] - 3, y.domain()[1] + 3])
            if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
                this.$refs.svg.createSVGPoint = null
            }
            let svg = d3.select(this.$refs.svg)
            let root = svg.selectAll('.root').data([null])
            root = root.merge(root.enter().append('g').attr('class', 'root'))
            this.updateAxes()
            let content = root.selectAll('.content').data([null])
            let contentEnter = content.enter().append('g').attr('class', 'content').attr('clip-path', 'url(#panel-content-clip)')
            contentEnter.append('rect').attr('class', 'background').on('mousemove', this.highlight).on('click', this.select)
            content = content.merge(contentEnter)
            content.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
            content.select('.background')
                .attr('width', this.size.width - this.margin.left - this.margin.right)
                .attr('height', this.size.height - this.margin.top - this.margin.bottom)
            let clippath = d3.select(this.$refs.svg).select('#panel-content-clip')
            clippath.select('rect')
                .attr('width', this.size.width - this.margin.left - this.margin.right)
                .attr('height', this.size.height - this.margin.top - this.margin.bottom)
            this.content = content
            this.updateLines()
        },
        updateAxes() {
            let root = d3.select(this.$refs.svg).select('.root')
            let xAxis = root.selectAll('.x-axis').data([null])
            let xAxisEnter = xAxis.enter().append('g').attr('class', 'axis x-axis')
            if (this.xAxisLabel === null)
                this.xAxisLabel = xAxisEnter.append('text').attr('class', 'label').text('Concentration (' + this.concentrationUnits + ')')
            else
                this.xAxisLabel.text('Concentration (' + this.concentrationUnits + ')')

            xAxis = xAxis.merge(xAxisEnter)
            xAxis.call(this.axisFuncs.x).attr('transform', `translate(${this.margin.left},${this.contentSize.height})`);
            xAxis.select('.label').attr("transform", `translate(${this.contentSize.width / 2},${this.margin.bottom - 10})`)

            let yAxis = root.selectAll('.y-axis').data([null])
            let yAxisEnter = yAxis.enter().append('g').attr('class', 'axis y-axis')
            yAxisEnter.append('text').attr('class', 'label').text('Block (%)')

            yAxis = yAxis.merge(yAxisEnter)
            yAxis.call(this.axisFuncs.y).attr('transform', `translate(${this.margin.left},${this.margin.top})`);
            yAxis.select('.label').attr("transform", `translate(-${this.margin.left - 30},${this.contentSize.height / 2}) rotate(-90)`)

            let grid = root.selectAll('.grid').data([null])
            let gridEnter = grid.enter().append('g').attr('class', 'grid')
            grid = grid.merge(gridEnter)

            let xlines = grid.selectAll('.x-line').data(this.axisFuncs.x.scale().ticks(10))
            xlines.enter().append('line').attr('class', 'x-line')
            xlines.exit().remove()

            let ylines = grid.selectAll('.y-line').data(this.axisFuncs.y.scale().ticks(this.isEditing ? 8 : 6))
            ylines.enter().append('line').attr('class', 'y-line')
            ylines.exit().remove()

            grid.attr("transform", `translate(${this.margin.left},${this.margin.top})`)
            grid.selectAll('.y-line').attr('x2', this.contentSize.width).attr('y1', (d) => this.axisFuncs.y.scale()(d)).attr('y2', (d) => this.axisFuncs.y.scale()(d))
            grid.selectAll('.x-line').attr('y2', this.contentSize.height).attr('x1', (d) => this.axisFuncs.x.scale()(d)).attr('x2', (d) => this.axisFuncs.x.scale()(d))
        },
        updateLines() {
            this.content.selectAll('.line').remove()
            let lineGenerator = d3.line()
                .x((d) => this.axisFuncs.x.scale()(d.c))
                .y((d) => this.axisFuncs.y.scale()(d.block));
            for (let i = 0; i < this.validationCurves.length; i++) {
                let line = this.content.append('path').attr('class', 'line').datum(this.validationCurves[i].data)
                line.attr('d', lineGenerator)
                    .attr('stroke-width',2.5)
                    .attr('stroke',this.colors[i])
            }

            // Add vertical lines on concentration X
            lineGenerator = d3.line()
                .x((d) => this.axisFuncs.x.scale()(d.x))
                .y((d) => this.axisFuncs.y.scale()(d.y));
            this.concentrations.forEach(concentrationDose => {
                let p1 = {x: concentrationDose, y: 0};
                let p2 = {x: concentrationDose, y: 100};
                let line = this.content.append('path').attr('class', 'line').datum([p1, p2])
                line.attr('d', lineGenerator)
                    .attr('stroke-dasharray', 2)
                    .attr('stroke', '#ff0000')
            });
        },
    },
    watch: {
        _size() {
            this.updateChart()
        },
        concentrations() {
            this.updateChart()
        },
        ic50Values() {
            this.updateChart()
        },
        hValues() {
            this.updateChart()
        },
        molecularWeight() {
            this.updateChart()
        }
    }
}
</script>

<style scoped lang="stylus">
.custom-drug-validation-plot
  padding-top: 0px
  position: relative

  .legendContainer
    display: flex
    flex-direction: row
    text-align: center
    margin: 0 auto
    .channel
        width: 70px
        margin: 10px auto

    .channel-label
        color: $shuttle-gray
        m-font-size(10, 12)
        padding-bottom: 10px
    
  svg
    >>> .root
      .line
        // pointer-events none
        fill: none
      .grid
        line
          stroke-width 0.2px
          stroke #444
      .axis
        .tick
          line
            stroke-width 0.2px
          text
            fill $shuttle-gray
        .domain
          stroke-width 0px
        .label
          font-size 12px
          fill $shuttle-gray
          opacity 0.7
      .content
        .background
          fill: #fff
          opacity 0
    >>> .brush-details
      .detail
        text
          fill #444
          font-size 12px
    

</style>